/*
  MAA cart — bike-information compatibility gate.
  Rebuilt 22 Aug 2026 for Horizon 4.1.4.

  WHY THIS IS AN ASSET AND NOT `{% javascript %}`.
  On 3.1.0 this lived in a `{% javascript %}` block inside
  `sections/main-cart.liquid`, a Horizon file — so every upgrade ate it. It is
  now MAA-owned and loaded by `<script src>`, which also sidesteps the fact that
  no Horizon 4.1.4 snippet uses `{% javascript %}` (83 use `{% stylesheet %}`;
  none use the JS tag).

  WHY A CUSTOM ELEMENT AND NOT `DOMContentLoaded`.
  4.1.4 wraps the whole cart page in `cart-items-component` and re-renders it
  through morph on every quantity change — measured: `.cart-page__summary` is
  inside that component. The 3.1.0 code bound its listeners once, to the element
  that existed at DOMContentLoaded. After the first quantity change that element
  is gone and the gate is bound to a detached node.

  `connectedCallback` runs again on every re-render, so the element re-arms
  itself. The checkout controls sit OUTSIDE this element and are re-rendered
  too, so that half stays delegated on `document` and is registered once.
*/

(() => {
  const FIELD_ID = 'maa-bike-info';
  const ATTRIBUTE = 'Bike information';
  const HIDDEN_NAME = `attributes[${ATTRIBUTE}]`;

  /*
    Typed-but-unsubmitted text, and how it is kept.

    MEASURED, not assumed. Incrementing the quantity on the 4.1.4 cart and then
    re-reading the field showed:
      - the <maa-bike-info> element is NOT replaced (morph patches in place), so
        connectedCallback does NOT run again — restoring on connect alone is
        not enough;
      - the textarea's text IS rewritten from the server response, so the typed
        value is silently discarded.

    Two independent mechanisms, because each covers what the other cannot:

    1. PERSIST to the cart attribute, debounced. This is the version-independent
       half. Once the value is on the cart, any re-render — morph today, some
       other mechanism after the next upgrade — renders it back, and it also
       survives a reload. It additionally fixes a real gap in the 3.1.0 design:
       the value only ever reached the order through hidden inputs on the cart
       form, so a rider checking out through Shop Pay or another accelerated
       button, which does not submit that form, could reach checkout with the
       gate satisfied and no bike information on the order.

    2. RESTORE on mutation. The network write is debounced, so a quantity change
       fired mid-typing can still repaint a stale value. A MutationObserver puts
       the draft back immediately. Setting `.value` changes the property, not
       the DOM, so this cannot loop.

    If the rider deliberately clears the field, `input` fires and sets draft to
    '' — so an empty draft is never restored over an intentional deletion.
  */
  let draft = null;
  let saveTimer = null;

  function persist() {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { [ATTRIBUTE]: value() } }),
      }).catch(() => {
        /* Never block the rider on this. The hidden fields and the gate below
           are the authoritative path for a normal form checkout. */
      });
    }, 500);
  }

  const field = () => document.getElementById(FIELD_ID);
  const value = () => (field()?.value ?? '').trim();

  /*
    Verified against the rendered 4.1.4 cart rather than carried over on faith —
    a gate that silently never fires is worse than one that is visibly missing.
    Measured on the candidate with a populated cart:
      button[name="checkout"]         1
      #checkout                       1
      .cart__ctas                     1
      .additional-checkout-buttons    1
      shopify-accelerated-checkout*   1
    `.shopify-payment-button` is 0 on the cart page (it is a product-page
    control) but is kept for the dynamic-checkout case.
  */
  const CHECKOUT_SELECTORS = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    'a[href*="/checkout"]',
    '.shopify-payment-button button',
    '.additional-checkout-buttons *',
  ].join(',');

  function syncHiddenFields() {
    const v = value();
    document.querySelectorAll('form[action*="/cart"]').forEach((form) => {
      let hidden = form.querySelector(`input[name="${HIDDEN_NAME}"]`);
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = HIDDEN_NAME;
        form.appendChild(hidden);
      }
      hidden.value = v;
    });
  }

  /*
    THE 3.1.0 BUG, FIXED.
    This block was referenced as a bare `reassurance`, which was never declared.
    Optional chaining does not protect an undeclared identifier, so every
    keystroke threw `ReferenceError: reassurance is not defined` — confirmed by
    dispatching an input event on the live cart and reading the console. The
    green "confirmed" state has therefore never worked on live.
  */
  function setConfirmed(on) {
    document
      .getElementById('maa-cart-reassurance')
      ?.classList.toggle('maa-cart-reassurance--confirmed', on);
  }

  function showError() {
    const el = field();
    document.getElementById('maa-bike-info-error')?.style.setProperty('display', 'block');
    el?.setAttribute('aria-invalid', 'true');
    el?.classList.add('maa-bike-info__field--error');
    document.querySelector('.maa-bike-info')?.classList.add('maa-bike-info--error');
    el?.focus();
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function clearError() {
    const el = field();
    document.getElementById('maa-bike-info-error')?.style.setProperty('display', 'none');
    el?.removeAttribute('aria-invalid');
    el?.classList.remove('maa-bike-info__field--error');
    document.querySelector('.maa-bike-info')?.classList.remove('maa-bike-info--error');
  }

  class MaaBikeInfo extends HTMLElement {
    connectedCallback() {
      const el = this.querySelector(`#${FIELD_ID}`);
      if (!el || this.dataset.maaBound === 'true') return;
      this.dataset.maaBound = 'true';

      if (draft !== null && el.value === '') el.value = draft;

      el.addEventListener('input', () => this.refresh(true));
      this.#watch(el);

      /* No argument: the initial pass syncs the counter and the hidden fields
         but must NOT write to the cart. On load the field already holds what
         the cart says, so persisting here would be a pointless request on every
         cart view. */
      this.refresh();
    }

    refresh(fromUser = false) {
      const el = this.querySelector(`#${FIELD_ID}`);
      if (!el) return;

      draft = el.value;

      const counter = this.querySelector('#maa-bike-info-count');
      if (counter) counter.textContent = String(el.value.length);

      syncHiddenFields();
      if (fromUser) persist();

      if (value()) {
        clearError();
        setConfirmed(true);
      } else {
        setConfirmed(false);
      }
    }

    /* See the note on `draft`. Morph rewrites this element's contents without
       replacing the element, so connectedCallback never runs again and this is
       the only hook that sees it happen.

       It has TWO jobs, and the second is easy to miss: the counter is rendered
       server-side as a literal 0, so after ANY re-render it reads 0 even when
       the textarea correctly shows the stored value. Measured: value restored,
       counter stuck on 0, confirmed state off. So this re-runs the full refresh
       rather than patching the value alone.

       The guard is what stops it looping — writing the counter is itself a
       mutation, so it must be a no-op once everything already agrees. */
    #watch(el) {
      const observer = new MutationObserver(() => {
        const counter = this.querySelector('#maa-bike-info-count');
        const needsRestore = draft && el.value !== draft;
        const needsResync = counter && counter.textContent !== String(el.value.length);
        if (!needsRestore && !needsResync) return;

        if (needsRestore) el.value = draft;
        this.refresh();
      });
      observer.observe(this, { childList: true, characterData: true, subtree: true });
    }
  }

  if (!customElements.get('maa-bike-info')) {
    customElements.define('maa-bike-info', MaaBikeInfo);
  }

  /*
    The gate itself. Registered once on `document`, in the capture phase, so it
    runs before Shopify's own handlers and before anything the accelerated
    checkout buttons attach. The `field()` guard means this is inert on every
    page that has no bike-info field — matching 3.1.0, which returned early when
    the element was absent.
  */
  if (!window.__maaCartGateBound) {
    window.__maaCartGateBound = true;

    document.addEventListener(
      'click',
      (event) => {
        if (!field()) return;
        const trigger = event.target instanceof Element && event.target.closest(CHECKOUT_SELECTORS);
        if (!trigger) return;

        syncHiddenFields();

        if (!value()) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          showError();
        }
      },
      true
    );

    document.addEventListener(
      'submit',
      (event) => {
        if (!field()) return;
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;

        const hasCheckoutControl =
          form.querySelector('button[name="checkout"], input[name="checkout"]') ||
          document.activeElement?.getAttribute?.('name') === 'checkout';
        if (!hasCheckoutControl) return;

        syncHiddenFields();

        if (!value()) {
          event.preventDefault();
          event.stopPropagation();
          showError();
        }
      },
      true
    );
  }
})();
